import axios from 'axios';
import Cookies from 'cookies-js';
import { get, post, apiErrorMessage } from '../../helpers/api';
import hitchApi from '../../helpers/api/hitchEndPointBase';

// HITCH-5299 exposes rider-driver exclusions (pairs ops has flagged not to
// ride together) as a plain REST resource, cursor-paginated like its
// trip-throttles sibling. Served under the admin app's global prefix, so the
// path is /admin/* like every other admin route.
const BASE = '/admin/rider-driver-exclusions';

const DEFAULT_LIMIT = 25;
const MAX_LIMIT = 100;

const getExclusions = async ({ status, reason, riderMongoId, driverMongoId, cursor, limit } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (reason) params.set('reason', reason);
  if (riderMongoId) params.set('riderMongoId', riderMongoId);
  if (driverMongoId) params.set('driverMongoId', driverMongoId);
  if (cursor) params.set('cursor', cursor);
  // Clamp client-side to the server's max rather than trusting callers not to
  // pass an unbounded limit.
  params.set('limit', String(Math.min(limit ?? DEFAULT_LIMIT, MAX_LIMIT)));

  const { data } = await get(`${BASE}?${params.toString()}`);
  return { entries: data?.entries ?? [], nextCursor: data?.nextCursor ?? null };
};

const createExclusion = async ({ riderMongoId, driverMongoId, reason, note }) => {
  try {
    const { data } = await post(BASE, { riderMongoId, driverMongoId, reason, note });
    return data;
  } catch (e) {
    // Rethrown as a plain Error so the server's 409 (duplicate active pair) /
    // 400 (self-pair, bad id) messages are catchable and renderable inline by
    // the create form, instead of a raw axios error object.
    throw new Error(apiErrorMessage(e) || 'Request failed');
  }
};

// Soft-remove: flips status to REMOVED. The DELETE carries an optional JSON
// body (removedNote), which the shared `del(endpoint)` helper does NOT send —
// so this goes through axios directly, mirroring the helper's URL construction
// and auth header (Authorization: the `user` cookie).
const removeExclusion = async ({ id, removedNote }) => {
  try {
    const { data } = await axios.delete(`${hitchApi}${BASE}/${id}`, {
      headers: { Authorization: Cookies.get('user') },
      data: { removedNote },
    });
    return data;
  } catch (e) {
    throw new Error(apiErrorMessage(e) || 'Request failed');
  }
};

// Loops every cursor page to build the FULL active set for client-side trip
// matching (HITCH-5300) — unlike the admin UI's page-at-a-time list, a single
// page here would silently miss every pair past the first once auto-population
// (v2) or a ratings backfill grows the registry into the hundreds/thousands.
const getAllActiveExclusions = async () => {
  const all = [];
  let cursor;
  for (;;) {
    const { entries, nextCursor } = await getExclusions({ status: 'ACTIVE', cursor, limit: MAX_LIMIT });
    all.push(...entries);
    if (!nextCursor) break;
    cursor = nextCursor;
  }
  return all;
};

export default {
  getExclusions,
  createExclusion,
  removeExclusion,
  getAllActiveExclusions,
};
